import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "../lib/api";

export default function SettingsPage() {
  const { t } = useTranslation();
  const [host, setHost] = useState("");
  const [port, setPort] = useState("3306");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [database, setDatabase] = useState("");
  const [existing, setExisting] = useState<{ host: string; port: number; username: string; database: string; lastTestedAt: string | null; lastTestOk: boolean | null; lastTestMessage: string | null } | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getDbConnection().then((res) => {
      if (res.connection) {
        setExisting(res.connection);
        setHost(res.connection.host);
        setPort(String(res.connection.port));
        setUsername(res.connection.username);
        setDatabase(res.connection.database);
      }
    });
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setTestResult(null);
    try {
      await api.saveDbConnection({ host, port: Number(port), username, password, database });
      setPassword("");
      const res = await api.getDbConnection();
      setExisting(res.connection);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await api.testDbConnection();
      setTestResult(result);
      const res = await api.getDbConnection();
      setExisting(res.connection);
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="page-padded">
      <h1>{t("settings.heading")}</h1>
      <p className="settings-intro">{t("settings.intro")}</p>

      <form className="card settings-form" onSubmit={handleSave}>
        <label className="field">
          {t("settings.host")}
          <input value={host} onChange={(e) => setHost(e.target.value)} required placeholder="db.example.internal" />
        </label>
        <label className="field">
          {t("settings.port")}
          <input value={port} onChange={(e) => setPort(e.target.value)} required inputMode="numeric" />
        </label>
        <label className="field">
          {t("settings.username")}
          <input value={username} onChange={(e) => setUsername(e.target.value)} required />
        </label>
        <label className="field">
          {t("settings.password")}
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={existing ? t("settings.passwordUnchanged") : ""} required={!existing} />
        </label>
        <label className="field">
          {t("settings.database")}
          <input value={database} onChange={(e) => setDatabase(e.target.value)} required />
        </label>

        {error && <p className="error-text">{error}</p>}

        <div className="settings-actions">
          <button className="btn-primary" type="submit" disabled={saving}>
            {t("settings.save")}
          </button>
          {existing && (
            <button type="button" onClick={handleTest} disabled={testing}>
              {t("settings.test")}
            </button>
          )}
        </div>

        {testResult && <p className={testResult.ok ? "settings-test-ok" : "error-text"}>{testResult.ok ? t("settings.testOk") : testResult.message}</p>}
        {existing?.lastTestedAt && !testResult && (
          <p className={existing.lastTestOk ? "settings-test-ok" : "error-text"}>
            {t("settings.lastTest", { date: new Date(existing.lastTestedAt).toLocaleString() })}: {existing.lastTestOk ? t("settings.testOk") : existing.lastTestMessage}
          </p>
        )}
      </form>
    </div>
  );
}
